/**
 * The slice of SuperSplat's Scene / Splat / Events that the ported renderer
 * touches, so projected-splat-renderer.ts compiles unchanged against the
 * viewer. Everything editor-only (selection, rings, colour panel) answers with
 * its neutral value from the Events shim.
 */

import {
    CameraComponent,
    Entity,
    EventHandler,
    GraphicsDevice,
    GSplatResource,
    StorageBuffer,
    Texture,
    type AppBase
} from 'playcanvas';

type FunctionCallback = (...args: any[]) => any;

class Events extends EventHandler {
    functions = new Map<string, FunctionCallback>();

    function(name: string, fn: FunctionCallback) {
        if (this.functions.has(name)) {
            throw new Error(`error: function ${name} already exists`);
        }
        this.functions.set(name, fn);
    }

    invoke(name: string, ...args: any[]) {
        const fn = this.functions.get(name);
        if (!fn) {
            console.error(`error: function not found '${name}'`);
            return;
        }
        return fn(...args);
    }
}

/** What the renderer reads from the editor's Scene. */
interface Scene {
    graphicsDevice: GraphicsDevice;
    app: AppBase;
    splatLayer: { id: number };
    events: Events;
    camera: { camera: CameraComponent; mainCamera: Entity; renderOverlays: boolean };
    targetSize: { width: number; height: number };
    movingRender: boolean;
}

/** The editor's per-gaussian instance list; here always the identity mapping. */
interface Instances {
    count: number;
    byteSize: number;
    numSelected: number;
    instanceSource: StorageBuffer;
    instanceFlags: StorageBuffer;
    instancePalette: StorageBuffer;
}

/** What the renderer reads from the editor's Splat. */
interface Splat {
    uid: number;
    visible: boolean;
    entity: Entity;
    resource: GSplatResource;
    instances: Instances;
    transformPalette: { texture: Texture };
    colorPalette: { texture: Texture };
    selectionAlpha?: number;
    projectedRendererStats?: unknown;
}

export { Events };
export type { Scene, Splat, Instances };
